import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import Bar from './components/Bar';

import { styles } from './index.styles';

const compareActionCount = (a, b) => b.actionCount - a.actionCount;

const KPIStatus = ({
  kpis = [],
  kpiStatusRef = [],
  top5KpisRef = [],
  selectedKPI,
  setSelectedKPI,
  bigText = true,
}) => {
  const { t } = useTranslation('planAction');

  const sortedKpis = useMemo(() => {
    const copy = [...kpis];
    copy.sort(compareActionCount);
    return copy;
  }, [kpis]);

  const sortedTargetStatements = useMemo(() => {
    if (sortedKpis[selectedKPI] && sortedKpis[selectedKPI].targetStatements) {
      const copy = [...sortedKpis[selectedKPI].targetStatements];
      copy.sort(compareActionCount);
      return copy;
    }
    return [];
  }, [selectedKPI]);

  const maxValue = useMemo(
    () => (sortedKpis.length ? sortedKpis[0].actionCount : 0),
    [sortedKpis],
  );

  const maxTargetStatementValue = useMemo(
    () =>
      sortedTargetStatements.length ? sortedTargetStatements[0].actionCount : 0,
    [sortedTargetStatements],
  );

  const topStatements = useMemo(
    () =>
      []
        .concat(...kpis.map((k) => k.targetStatements))
        .sort(compareActionCount)
        .slice(0, 5),
    [kpis],
  );

  const maxTopStatementValue = useMemo(
    () => (topStatements.length ? topStatements[0].actionCount : 0),
    [topStatements],
  );

  useEffect(() => {
    if (sortedKpis.length) {
      setSelectedKPI(0);
    }
  }, [sortedKpis, setSelectedKPI]);

  return (
    <Box>
      <Grid
        container
        ref={kpiStatusRef}
        sx={{
          mb: 1,
        }}
      >
        <Grid item xs={6}>
          <Box sx={{ height: '100%', ...styles.tile }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: bigText ? 30 : 22,
                mb: 2,
              }}
            >
              {t('Statistics.Kpis.ActionsCreated')}
            </Typography>
            {sortedKpis.map((kpi, index) => (
              <Bar
                key={kpi.importId}
                max={maxValue}
                selected={sortedKpis[selectedKPI].importId === kpi.importId}
                onSelect={() => setSelectedKPI(index)}
                data-test-id="kpi-bar"
                bigText={bigText}
                {...kpi}
              />
            ))}
          </Box>
        </Grid>
        <Grid item xs={6} data-test-id="target-statements">
          <Box sx={{ height: '100%', ...styles.tile }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: bigText ? 30 : 22,
                mb: 2,
              }}
            >
              {t('Statistics.Kpis.ActionsPerTargetStatement')}
            </Typography>
            {sortedTargetStatements.map((statement) => (
              <Bar
                key={statement.importId}
                max={maxTargetStatementValue}
                bigText={bigText}
                {...statement}
              />
            ))}
          </Box>
        </Grid>
      </Grid>
      <Box ref={top5KpisRef} sx={styles.tile}>
        <Typography
          variant="h6"
          sx={{
            fontSize: bigText ? 30 : 22,
            mb: 2,
          }}
        >
          {t('Statistics.Kpis.Top5Statements')}
        </Typography>
        <Box>
          {topStatements.map((statement) => (
            <Bar
              key={statement.importId}
              max={maxTopStatementValue}
              data-test-id="top5-statements"
              bigText={bigText}
              {...statement}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

KPIStatus.propTypes = {
  kpis: PropTypes.arrayOf(
    PropTypes.shape({
      importId: PropTypes.string,
      label: PropTypes.string,
      actionCount: PropTypes.number,
      targetStatements: PropTypes.arrayOf(
        PropTypes.shape({
          importId: PropTypes.string,
          label: PropTypes.string,
          actionCount: PropTypes.number,
        }),
      ),
    }),
  ),
  kpiStatusRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  top5KpisRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  selectedKPI: PropTypes.shape({
    importId: PropTypes.string,
    label: PropTypes.string,
    actionCount: PropTypes.number,
    targetStatements: PropTypes.arrayOf(
      PropTypes.shape({
        importId: PropTypes.string,
        label: PropTypes.string,
        actionCount: PropTypes.number,
      }),
    ),
  }).isRequired,
  setSelectedKPI: PropTypes.func.isRequired,
  bigText: PropTypes.bool,
};

export default KPIStatus;
